import PlusIcon from '@activities/prepaidCard/MyPrepaidCardActivity/components/Blank/PlusIcon';
import Column from '@components/Column';
import Image from '@components/Image';
import Text from '@components/Text';
import colors from '@constants/colors';
import styled from '@emotion/styled';
import { FavoritePrepaidCardRow } from '@models/prepaidCard';
import { Link } from '@stackflow/link';
import { useFlow } from '@stackflow/react';
import { pressActionEffect } from '@styles/mixin';
import { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { A11y, Keyboard } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import useRolling from './hooks/useRolling';
import settingIcon from '../../assets/ic_setting.svg';

interface Props {
    cards: FavoritePrepaidCardRow[];
}

function MultipleCard({ cards }: Props) {
    const { t } = useTranslation('prepaidCard');
    const { move } = useRolling({ cards });
    const { push } = useFlow();

    const handleSettingsClick = (e: MouseEvent, card: FavoritePrepaidCardRow) => {
        e.preventDefault();
        e.stopPropagation();
        push('CardManageBottomSheet', { prepaidCardId: card.id, cardName: card.name });
    };

    const handleCardClick = (card: FavoritePrepaidCardRow) => {
        push('PrepaidCardBarcodeBottomSheet', { prepaidCardId: card.id });
    };

    return (
        <Container fullWidth align='center'>
            <CardSwiper key={cards.map((card) => card.id).join(',')}
                modules={[A11y, Keyboard]}
                keyboard={{ enabled: true }}
                centeredSlides
                slidesPerView='auto'
                spaceBetween={24}
                onSlideChange={(swiper) => move(swiper.activeIndex)}>
                {cards.map((card, index) => (
                    <CardSlide key={card.id}>
                        <Card>
                            <CardButton type='button' onClick={() => handleCardClick(card)}
                                aria-label={`${card.name} ${t('barcode.title')}`}>
                                <CardImage src={card.imageUrl} alt={t('multipleCard.imageAlt')} />
                            </CardButton>
                            <SettingsButton type='button'
                                onClick={(event) => handleSettingsClick(event, card)}
                                css={pressActionEffect}
                                aria-label={t('manageSheet.title')}>
                                <SettingsIcon src={settingIcon} width={18} height={18} alt='' />
                            </SettingsButton>
                            <IndicatorBadge>{index + 1}/{cards.length}</IndicatorBadge>
                        </Card>
                    </CardSlide>
                ))}
                <CardSlide>
                    <BuyLink activityParams={{ type: 'BUY' }}
                        activityName='PrepaidCardImagesActivity'>
                        <BuyCard justify='center' align='center' spacing={2}>
                            <PlusIcon />
                            <Text size='title' color={colors.textSecondary} fontWeight='medium'>
                                {t('blank.buyNewCard')}
                            </Text>
                        </BuyCard>
                    </BuyLink>
                </CardSlide>
            </CardSwiper>
        </Container>
    );
}

const Container = styled(Column)`
    background-color: var(--color-white);
    padding: 24px 0;
`;

const CardSwiper = styled(Swiper)`
    width: 100%;
`;

const CardSlide = styled(SwiperSlide)`
    position: relative;
    transition: transform 0.3s ease;
    width: calc(100% - 100px);

    /* swiper.css 의 .swiper-ios .swiper-slide { transform } 보다 우선해야 한다. */
    && {
        transform: scale(0.88);
    }

    &&.swiper-slide-active {
        transform: scale(1);
    }

    &.swiper-slide-prev {
        transform-origin: right center;
    }

    &.swiper-slide-next {
        transform-origin: left center;
    }
`;
// swiper/react 는 displayName 에 'SwiperSlide' 가 있어야 슬라이드로 인식한다. (emotion label 이 displayName 을 덮어씀)
CardSlide.displayName = 'SwiperSlide';

const CardButton = styled.button`
    display: block;
    width: 100%;
    height: 100%;
    padding: 0;
    background: transparent;
    border-radius: 4px;
    overflow: hidden;
`;

const Card = styled.div`
    aspect-ratio: 3 / 2;
    overflow: hidden;
    position: relative;
    width: 100%;
`;

const BuyLink = styled(Link)`
    display: block;
    text-decoration: none;
    width: 100%;
`;

const BuyCard = styled(Column)`
    aspect-ratio: 3 / 2;
    background-color: var(--color-white);
    border: 2px dashed var(--color-gray4);
    border-radius: 12px;
    cursor: pointer;
    width: 100%;
`;

const CardImage = styled(Image)`
    display: block;
    height: 100%;
    object-fit: contain;
    width: 100%;
`;

const SettingsButton = styled.button`
    align-items: center;
    background: var(--color-white);
    border-radius: 20px;
    display: flex;
    height: 32px;
    justify-content: center;
    padding: 4px;
    position: absolute;
    right: 10px;
    top: 10px;
    width: 32px;
`;

const SettingsIcon = styled.img`
    display: block;
    height: 24px;
    width: 24px;
`;

const IndicatorBadge = styled.span`
    position: absolute;
    right: 8px;
    bottom: 11px;
    padding: 4px;
    border-radius: 4px;
    background: rgba(0, 0, 0, 0.5);
    color: white;
    font-size: 12px;
    font-weight: 500;
    line-height: 1.4;
    pointer-events: none;
`;

export default MultipleCard;
